import { Button } from '@/components/atoms/Button'
import { Label } from '@/components/atoms/Label'
import { Panel } from '@/components/atoms/Panel'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/atoms/Table'
import { Page } from '@/components/molecules'
import { routes } from '@/config'
import { trpc } from '@/config/trpc'
import { useSearchState } from '@/hooks/useSearchState'
import { useMemo } from 'react'
import { NavLink } from 'react-router'
import { CreateApiKey } from './components/CreateApiKey'
import { CustomerList } from './components/CustomerList'

export const USER_UUID_KEY = 'user_uuid'

type CustomerSearchState = { uuid: string }

function ApiKeysPage() {
  const [users, { refetch }] = trpc.getApiUsers.useSuspenseQuery()

  const { searchState, updateSearchState } = useSearchState()
  const handleCustomerChange = ({ uuid }: CustomerSearchState) => updateSearchState([USER_UUID_KEY, uuid])
  const selectedCustomer = useMemo(() => ({ uuid: searchState.get(USER_UUID_KEY) ?? '' }), [searchState])

  const selectedCustomerWithData = useMemo(
    () => users.find((user) => user.uuid === selectedCustomer.uuid),
    [selectedCustomer, users]
  )

  return (
    <Page title="Update data">
      {selectedCustomer.uuid ? (
        <div className="flex h-fit flex-col gap-6" data-role="left-column">
          <Button className="w-full gap-2" onClick={() => handleCustomerChange({ uuid: '' })}>
            Show Customer List
          </Button>
          <Panel className="flex flex-col gap-4 p-4">
            <div>
              <Label>Name</Label>
              <span className="text-xl">{selectedCustomerWithData?.name}</span>
            </div>
            <div>
              <Label>UUID</Label>
              <span className="text-xl">{selectedCustomerWithData?.uuid}</span>
            </div>
            <div>
              <Label>API Keys</Label>

              <TableBody>
                <TableHeader>
                  <TableRow
                    className="grid grid-cols-[450px_450px_80px_50px] items-center gap-1"
                    data-role="protocol-info"
                  >
                    <TableHead>Name</TableHead>
                    <TableHead>Key Hash</TableHead>
                    <TableHead className="w-full text-wrap text-left">Requests per minute</TableHead>
                    <TableHead>Is pro</TableHead>
                  </TableRow>
                </TableHeader>
                {selectedCustomerWithData?.api_keys.map(({ name, is_pro, key_hash, requests_per_minute }) => (
                  <TableRow key={key_hash}>
                    <TableCell className="px-2">
                      <div className="grid grid-cols-[450px_450px_80px_50px] items-center" data-role="protocol-info">
                        <span className="text-left" title="name">
                          {name}
                        </span>
                        <span className="text-left" title="hash">
                          {key_hash}
                        </span>
                        <span className="text-left" title="requests">
                          {requests_per_minute}
                        </span>
                        <span className="text-left" title="isPro">
                          {is_pro ? 'True' : 'False'}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </div>
          </Panel>

          <CreateApiKey customerUUID={selectedCustomer.uuid} refetch={refetch} />
        </div>
      ) : (
        <div className="flex h-fit flex-col gap-6" data-role="left-column">
          <Button className="w-full gap-2" asChild>
            <NavLink to={routes.addCustomer}>
              <span>Add customer</span>
            </NavLink>
          </Button>
          <CustomerList
            customers={users}
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={handleCustomerChange}
          />
        </div>
      )}
    </Page>
  )
}

export const Component = () => ApiKeysPage()
